import { useQueries } from '@tanstack/react-query';
import { Link, Navigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { CreateWorkspaceDialog } from '@/components/create-workspace-dialog';
import { Logo } from '@/components/logo';
import { EmptyState, ErrorState, LoadingPage } from '@/components/states';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useMe } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

export const LAST_WORKSPACE_KEY = 'deskworks.lastWorkspace';

export function readLastWorkspace(): string | null {
  try {
    return localStorage.getItem(LAST_WORKSPACE_KEY);
  } catch {
    return null;
  }
}

export function IndexRedirect() {
  const me = useMe();
  const accounts = me.data?.accounts ?? [];
  const wsQueries = useQueries({
    queries: accounts.map((a) => ({
      queryKey: qk.accountWorkspaces(a.id),
      queryFn: () => api.accounts.workspaces(a.id),
    })),
  });

  if (me.isPending) return <LoadingPage />;
  if (me.isError) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </div>
    );
  }
  if (wsQueries.some((q) => q.isPending)) return <LoadingPage />;
  const failed = wsQueries.find((q) => q.isError);
  if (failed && wsQueries.every((q) => !q.data?.length)) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <ErrorState error={failed.error} onRetry={() => void failed.refetch()} />
      </div>
    );
  }

  // Landing page: the account-wide desk overview of the first account that has a workspace.
  const target = accounts.find((_, i) => (wsQueries[i]?.data?.length ?? 0) > 0);
  if (target) return <Navigate to="/$account/desks" params={{ account: target.slug || target.id }} replace />;
  // Someone who came by a desk's link (docs/features/desk-sharing.md): their desk is their place.
  const shared = me.data?.guestDesks ?? [];
  if (accounts.length === 0 && shared.length === 1) return <Navigate to="/guest/$deskId" params={{ deskId: shared[0].deskId }} replace />;

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex justify-center">
          <Logo className="size-10" />
        </div>
        {accounts.length === 0 && shared.length > 1 ? (
          <div className="grid gap-2">
            <h1 className="mb-2 text-center text-lg font-semibold">{t('Desks shared with you')}</h1>
            {shared.map((d) => (
              <Link key={d.deskId} to="/guest/$deskId" params={{ deskId: d.deskId }} className="flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-muted/50">
                <Logo src={d.branding?.logoUrl} className="size-8" />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{d.deskName}</span>
                  <span className="block truncate text-xs text-muted-foreground">{d.accountName}</span>
                </span>
              </Link>
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <EmptyState
            icon={<Building2 />}
            title={t('You’re not part of an Account yet')}
            description={t('Ask an admin to invite you to an Account. After that, you’ll see your workspaces here.')}
          />
        ) : (
          <EmptyState
            icon={<Building2 />}
            title={t('No workspace yet')}
            description={t('A Workspace is an area of your company, e.g. “Marketing” or “Support”. Create your first one to get started.')}
            action={
              <CreateWorkspaceDialog accounts={accounts}>
                <Button>{t('Create workspace')}</Button>
              </CreateWorkspaceDialog>
            }
          />
        )}
      </div>
    </div>
  );
}
